/**
 * Conferma di cancellazione di un documento.
 *
 * Perché una conferma e non un click secco: su SharePoint la cancellazione fatta
 * dall'identità applicativa non passa dal cestino dell'utente, e l'unica traccia
 * di *chi* l'ha chiesta è l'audit log del portale (spec §8.5). Un click per
 * sbaglio non è recuperabile dal cliente.
 *
 * Il nome del file va scritto per esteso: in una lista di venti fatture con nomi
 * quasi identici, «Confermi l'eliminazione?» non dice quale. Vale anche per la
 * cancellazione in gruppo, dove l'elenco sostituisce il nome singolo.
 */
import { ApiError } from '../lib/api.js';
import type { Entry } from '../lib/types.js';
import './DeleteEntryDialog.css';

export function DeleteEntryDialog({
  entries,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  /** Uno o più: la conferma è la stessa, cambiano il titolo e l'elenco. */
  entries: Entry[];
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const molti = entries.length > 1;
  const primo = entries[0];
  const isFolder = !molti && primo?.kind === 'folder';

  if (!primo) return null;

  return (
    <div className="delentry" role="dialog" aria-modal="true" aria-labelledby="delentry-title">
      <div className="delentry__box card">
        <h3 id="delentry-title">
          {molti
            ? `Eliminare ${entries.length} documenti?`
            : isFolder
              ? 'Eliminare questa cartella?'
              : 'Eliminare questo documento?'}
        </h3>

        {/*
          I nomi per esteso anche in gruppo: in una lista di venti fatture con
          nomi quasi identici, «3 documenti» non dice quali. Oltre i dieci
          l'elenco diventa un muro e si passa al conteggio della coda.
        */}
        {molti ? (
          <ul className="delentry__lista">
            {entries.slice(0, 10).map((e) => (
              <li key={e.ref} title={e.name}>
                {e.name}
              </li>
            ))}
            {entries.length > 10 ? <li>…e altri {entries.length - 10}</li> : null}
          </ul>
        ) : (
          <p className="delentry__name" title={primo.name}>
            {primo.name}
          </p>
        )}

        <p className="delentry__warn">
          {isFolder
            ? 'La cartella e tutto il suo contenuto verranno rimossi. L\'operazione non è annullabile dal portale.'
            : 'L\'operazione non è annullabile dal portale.'}
        </p>

        {error ? (
          <div className="alert alert--error delentry__error">
            {error instanceof ApiError ? error.message : 'Eliminazione non riuscita.'}
          </div>
        ) : null}

        <div className="delentry__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={pending}>
            Annulla
          </button>
          <button type="button" className="btn btn--danger" onClick={onConfirm} disabled={pending}>
            {pending ? 'Eliminazione…' : molti ? `Elimina ${entries.length}` : 'Elimina'}
          </button>
        </div>
      </div>
    </div>
  );
}
